import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { ConfirmationModalType } from 'app/shared/components/confirmation-modal';
import { CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { ConfirmationService } from 'app/shared/services/confirmation.service';
import { forkJoin } from 'rxjs';
import { BusinessPartnerBySubRoute } from '../../../../interfaces/busines-partner-by-sub-route.interface';
import { SubRouteFrequenciesBPService } from '../../../../sub-routes-frequency/sub-route-frequencies-bp.service';
import { SubRouteFrequenciesService } from '../../../../sub-routes-frequency/sub-route-frequency.service';

/**
 * Cell renderer params with delete-specific properties
 */
interface DeleteCellRendererParams extends CellRendererParams<BusinessPartnerBySubRoute> {
  getSubRouteId: () => string | null;
  onDeleteSuccess: () => void;
}

@Component({
  selector: 'app-delete-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, TranslatePipe],
  template: `
    <button
      mat-icon-button
      color="warn"
      class="!h-8 !w-8"
      (click)="onDelete()"
      [matTooltip]="'routing.SUB_ROUTE_PARTNERS.LIST.DELETE.TOOLTIP' | t"
    >
      <mat-icon class="!text-lg">delete</mat-icon>
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteCellRendererComponent {
  private confirmationService = inject(ConfirmationService);
  private handlerMessage = inject(HandlerMessageService);
  private subRouteFrequenciesBPService = inject(SubRouteFrequenciesBPService);
  private appointmentBPService = inject(SubRouteFrequenciesService);
  private i18n = inject(LanguageService);

  readonly params = input.required<DeleteCellRendererParams>();

  readonly data = computed(() => this.params()?.data);

  // Callbacks from params
  readonly onDeleteSuccess = computed(() => this.params()?.onDeleteSuccess);

  // Get subRouteId by calling the getter function
  private getSubRouteId(): string | null {
    return this.params()?.getSubRouteId?.() ?? null;
  }

  onDelete(): void {
    const bpData = this.data();
    if (!bpData) return;

    const customerName = bpData.name ?? this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.DELETE.FALLBACK_NAME');

    this.confirmationService
      .confirm({
        title: this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.DELETE.TITLE'),
        description: this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.DELETE.DESCRIPTION', { customerName }),
        type: ConfirmationModalType.DANGER,
        confirmButtonText: this.i18n.t('CONFIRM.DELETE.CONFIRM'),
        cancelButtonText: this.i18n.t('GENERAL.CANCEL'),
        descriptionIcon: 'person_remove',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.performDelete(bpData);
        }
      });
  }

  private performDelete(element: BusinessPartnerBySubRoute): void {
    const subRouteId = this.getSubRouteId();
    if (!subRouteId) {
      this.handlerMessage.error({ message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.DELETE.NO_SUBROUTE_ID') });
      return;
    }

    const frequencies = element.frequenciesFromThisSubRoute || [];
    const typeWIds = frequencies.filter((item) => item.type === 'W').map((item) => item._id);
    const typeCIds = frequencies.filter((item) => item.type === 'C').map((item) => item._id);

    if (typeWIds.length === 0 && typeCIds.length === 0) {
      this.handlerMessage.warning({
        message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.DELETE.NO_RECORDS'),
      });
      return;
    }

    // Create observables for type W deletions
    const typeWObservables = typeWIds.map((frequencyId) =>
      this.subRouteFrequenciesBPService.deleteBusinessPartnerFromFrecuency(
        subRouteId,
        frequencyId,
        element._id
      )
    );

    // Create observable for type C deletion if needed
    const typeCObservables =
      typeCIds.length > 0
        ? [
            this.appointmentBPService.deleteManyCompleteBusinessPartnerAppointment(
              subRouteId,
              element._id
            ),
          ]
        : [];

    // Combine all observables
    const allObservables = [...typeWObservables, ...typeCObservables];

    if (allObservables.length === 0) {
      return;
    }

    forkJoin(allObservables).subscribe({
      next: () => {
        this.handlerMessage.success({
          message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.DELETE.SUCCESS'),
        });
        this.onDeleteSuccess()?.();
      },
      error: (error) => {
        this.handlerMessage.error({
          message: error?.error?.message || this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.DELETE.ERROR'),
        });
      },
    });
  }
}
